// score.ts — Principle Three ("the future is never mortgaged for the benefit of
// the present") made countable, as the NexusOps Economic Modeling Sandbox asks.
//
// This is the bridge from the AI-capex contagion model to the Nexuspolis
// sequence: the depreciation wall the model already computes IS an
// intergenerational-mortgage argument. A debt-financed build creates a burden —
// locked depreciation plus committed interest — that must be serviced against
// future revenue that may not arrive. When demand collapses (the "winter"
// preset) the burden does not unwind while the earnings that cover it do. That
// asymmetry is exactly what "mortgaging the future" means on a balance sheet.
//
// PURE, like solve(): (Drivers, World) in, a derived score out. No I/O, no
// clock, no globals. The score is INTERPRETIVE and derived — it introduces no
// new filed data, and the UI/methodology must present it as such (in the spirit
// of the model's ESTIMATE flags), never as a measured fact.
//
// The score is deliberately NOT an INV-1 quantity: it does not reconcile to
// zero at base. Even at base these builders carry real debt-financed, locked
// stock, so the base case has a real, non-zero mortgage reading. Its invariants
// (INV-P3, tests/score.test.ts) are directional and bounded, not zero-seeking.

import type { Drivers, World } from "./entities.ts";
import { solve, type HypResult, type Result } from "./solve.ts";

const EPS = 1e-9;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/** The three countable sub-questions of Principle Three, each in [0,1]. */
export interface MortgageComponents {
  /** Share of the unavoidable burden (locked depreciation + interest) in the
   *  sum of that burden and residual profit. Rises as profit collapses — the
   *  depreciation-wall reading. 1 = the burden dwarfs what is left to cover it. */
  fragility: number;
  /** Locked depreciation as a share of total depreciation: the fraction of the
   *  build already in service and impossible to switch off by cutting capex. */
  lock: number;
  /** Interest on new debt as a share of interest + base operating profit: how
   *  much of present earnings is pre-committed to servicing the financed build. */
  leverage: number;
}

/** Principle-Three reading for one capex carrier. `score` ∈ [0,100]; higher =
 *  more of the future mortgaged for the present. */
export interface EntityMortgageScore {
  id: string;
  name: string;
  rating: string;
  components: MortgageComponents;
  /** $B of unavoidable annual burden: locked depreciation + interest on new debt. */
  burden: number;
  /** Scenario operating profit, $B (the earnings available to carry the burden). */
  profit: number;
  /** 0–100 composite (equal-weighted mean of the three components × 100). */
  score: number;
}

/** Weights over the three components. Equal by default — the most defensible
 *  choice per the OECD/JRC composite-indicator guidance the Nexuspolis Index
 *  track adopts. Exposed so a sensitivity pass can re-weight. Must sum to 1. */
export interface MortgageWeights {
  fragility: number;
  lock: number;
  leverage: number;
}
export const EQUAL_WEIGHTS: MortgageWeights = { fragility: 1 / 3, lock: 1 / 3, leverage: 1 / 3 };

export interface PrincipleThreeScore {
  /** Per-carrier readings, most-mortgaged first. */
  entities: EntityMortgageScore[];
  /** Capex-weighted mean of the entity scores, 0–100 — the bigger the build,
   *  the more it weighs on the system reading. */
  systemScore: number;
  /** System coverage ratio: total scenario profit / total unavoidable burden.
   *  Below 1 means the locked burden exceeds the earnings covering it. This is
   *  the number INV-P3 ties to INV-2 (the depreciation wall). */
  systemCoverage: number;
  /** The weights used, echoed for transparency. */
  weights: MortgageWeights;
  /** A standing reminder that this reading is derived and interpretive. */
  note: string;
}

const NOTE =
  "Derived, interpretive reading — not a filed figure. Principle Three scores " +
  "how far a debt-financed, locked build mortgages future earnings; it inherits " +
  "the model's estimated parameters and should be challenged like any estimate.";

/** Principle-Three reading for one solved carrier. */
function scoreEntity(h: HypResult, w: MortgageWeights): EntityMortgageScore {
  const burden = h.lockedDep + h.interest; // unavoidable: does not unwind on a capex cut
  const fragility = clamp01(burden / (burden + Math.max(h.profit, 0) + EPS));
  const lock = clamp01(h.lockedDep / (h.dep + EPS));
  const leverage = clamp01(h.interest / (h.interest + Math.max(h.profitBase, 0) + EPS));
  const components: MortgageComponents = { fragility, lock, leverage };
  const score = 100 * (w.fragility * fragility + w.lock * lock + w.leverage * leverage);
  return {
    id: h.id,
    name: h.name,
    rating: h.rating,
    components,
    burden,
    profit: h.profit,
    score,
  };
}

/** Score a solved Result against Principle Three. Split out so callers that
 *  already hold a Result (the browser's render loop) need not re-solve. */
export function scoreResult(r: Result, weights: MortgageWeights = EQUAL_WEIGHTS): PrincipleThreeScore {
  const entities = r.hyp.map((h) => scoreEntity(h, weights)).sort((a, b) => b.score - a.score);
  const capexTotal = r.hyp.reduce((a, h) => a + h.capex, 0) || 1;
  const systemScore = r.hyp.reduce((a, h) => {
    const e = entities.find((x) => x.id === h.id)!;
    return a + h.capex * e.score;
  }, 0) / capexTotal;
  const burdenTotal = r.hyp.reduce((a, h) => a + h.lockedDep + h.interest, 0);
  const profitTotal = r.hyp.reduce((a, h) => a + h.profit, 0);
  const systemCoverage = profitTotal / (burdenTotal + EPS);
  return { entities, systemScore, systemCoverage, weights, note: NOTE };
}

/** Score a driver state against Principle Three. Pure: (Drivers, World) → score. */
export function scorePrincipleThree(
  st: Drivers,
  w: World,
  weights: MortgageWeights = EQUAL_WEIGHTS,
): PrincipleThreeScore {
  return scoreResult(solve(st, w), weights);
}
